import { defineStore } from "pinia";
import { StationGroup } from "~/api/models/station-group.model";

import { Station } from "../api/models/station.model";
import { StationsStoreError } from "~/exceptions/store.error";
import { getStationAlarms, isStationInGasAlarm } from "~/misc/station-utils";
import moment from "moment";

export const SessionStorageCurrentGroupKey: string = 'groupId';

export const useStationStore = defineStore('station', {
    state: () => {
        return {
            stations: [] as Station[],
            groups: [] as StationGroup[],
            group: null as StationGroup,
            default: null as StationGroup,
            ready : false,
        };
    },

    actions: {

        async selectStationGroup(groupName: string): Promise<void> {

            const groups = this.groups;

            const group: StationGroup = groups.find((group: StationGroup) => group.name === groupName);

            if (group) {
                this.group = group;
            } else {
                throw new StationsStoreError(`Group ${group} does not exist`)
            }

            if(group?.config?.timezone && moment.tz.names().includes(group?.config?.timezone)) {
                moment.tz.setDefault(group?.config?.timezone);
            } else {
                moment.tz.setDefault('Europe/Paris');
            }


            localStorage.setItem(SessionStorageCurrentGroupKey, group?.name);
            await this.refresh();
        },

        async init(): Promise<void> {

            if(this.ready) {
                return;
            }

            try {
                await this.fetchStationGroups();
                this.default = this.groups.find(grp => grp.name === localStorage.getItem(SessionStorageCurrentGroupKey));

                if(!this.default) {
                    this.default = this.groups[0];
                }

                this.ready = true;
            } catch (error) {
                throw new StationsStoreError(`Could not refresh stations`, error);
            }
        },

        async refresh(): Promise<void> {
           
            if (!this.group) {
                throw new StationsStoreError(`No group selected`);
            }

            try {
                await this.fetchStations();
            } catch (error) {
                throw new StationsStoreError(`Could not refresh stations`, error);
            }
        },

        async fetchStationGroups(): Promise<StationGroup[]> {
            this.groups = await this.$stationService.getUserGroups();
            return this.groups;
        },

        async fetchStations(): Promise<Station[]> {

            if (!this.group) {
                throw new Error('group is required to fetch stations');
            }

            return this.stations = await this.$stationService.getStationsByGroup(this.group.name)
                .then(stations => Promise.all(stations.map(s => this.$stationService.getStationDetails(s?.id))))
                .then(stations => {
                    return stations.sort((dev1, dev2) => {
                        const dev1Alarms = getStationAlarms(dev1).map(a => a.name);
                        const dev2Alarms = getStationAlarms(dev2).map(a => a.name);
                        if(isStationInGasAlarm(dev1)) {
                            return -1;
                        } else if(isStationInGasAlarm(dev2)) {
                            return 1;
                        } else if(dev1Alarms.length > 0 || dev2Alarms.length > 0) {
                            return dev2Alarms.length - dev1Alarms.length;
                        } else {
                            return dev2.last_seen - dev1.last_seen;
                        }
                    });
            });
        },



        async updateStation(stationId: string, station: Partial<Station>): Promise<Station> {
            const result = await this.$stationService.updateStation(stationId, station);
            await this.fetchStations();
            return result;
        },
    }
})